import React, { useEffect, useRef } from 'react';
import { 
  Box, Typography, Grid, MenuItem, Select, FormControl,
  Stack, Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCommodityOptionChain } from '../hooks/useCommodityOptionChain';
import OptionListCard from '../../IndexFnO/components/OptionListCard';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { ListRowSkeleton } from '../../../components/DashboardSkeletons';
import { formatSmartOptionExpiry } from '../../../utils/expiryUtils';

const CommodityOptionChainWidget = ({ 
  selectedCommodity, 
  activeFuture, 
  liveSpotPrice,
  onEmptyChain,
  onOptionsReady
}) => {
  const theme = useTheme();
  const {
    loading,
    resolvedToken,
    optionChain,
    expiries,
    selectedExpiry,
    setSelectedExpiry
  } = useCommodityOptionChain(selectedCommodity, activeFuture, liveSpotPrice);

  // Default to first expiry if none selected
  const currentExpiryValue = selectedExpiry || (expiries.length > 0 ? expiries[0] : '');
  const spotPrice = liveSpotPrice || activeFuture?.ltp || activeFuture?.close_price || 0;

  const filteredData = React.useMemo(() => {
    if (!optionChain || optionChain.length === 0) return [];
    const sorted = [...optionChain].sort((a, b) => a.strike_price - b.strike_price);
    let atmIndex = 0;
    let minDiff = Infinity;
    sorted.forEach((row, i) => {
      const diff = Math.abs(row.strike_price - spotPrice);
      if (diff < minDiff) {
        minDiff = diff;
        atmIndex = i;
      }
    });
    const start = Math.max(0, Math.min(sorted.length - 5, atmIndex - 2));
    const end = Math.min(sorted.length, start + 5);
    return sorted.slice(start, end);
  }, [optionChain, spotPrice]);

  const callData = filteredData;
  const putData = [...filteredData].sort((a, b) => b.strike_price - a.strike_price);

  // Track which token we've already notified the parent about
  const hasNotifiedRef = useRef(null);

  // Detect empty/loaded option chain ONLY when the hook has confirmed
  // it finished loading for the current token (resolvedToken matches).
  // This prevents the race condition where stale data from a previous
  // token triggers a false "empty" detection during token transitions.
  useEffect(() => {
    if (loading) return;
    if (!resolvedToken || resolvedToken !== activeFuture?.token) return;
    if (hasNotifiedRef.current === resolvedToken) return;

    const isEmpty = optionChain.length === 0 && expiries.length === 0;

    if (isEmpty && onEmptyChain) {
      hasNotifiedRef.current = resolvedToken;
      onEmptyChain();
    } else if (!isEmpty && onOptionsReady) {
      hasNotifiedRef.current = resolvedToken;
      onOptionsReady();
    }
  }, [loading, resolvedToken, activeFuture?.token, optionChain.length, expiries.length, onEmptyChain, onOptionsReady]);

  // Determine the display state to avoid "No Options" flicker
  const showLoading = loading || (expiries.length === 0 && !optionChain);
  const hasData = expiries.length > 0 && optionChain.length > 0;

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        gap: 2
      }}>
        <Typography variant="subtitle2" fontWeight={800} color="text.primary">
          OPTIONS
        </Typography>

        {currentExpiryValue && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
              EXPIRY:
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {formatSmartOptionExpiry(currentExpiryValue)}
            </Typography>
          </Stack>
        )}
      </Box>

      {loading ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} lg={6}>
            <ListRowSkeleton count={5} rowHeight={56} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ListRowSkeleton count={5} rowHeight={56} />
          </Grid>
        </Grid>
      ) : hasData ? (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <OptionListCard side="call" data={callData} spotPrice={spotPrice} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <OptionListCard side="put" data={putData} spotPrice={spotPrice} />
          </Grid>
        </Grid>
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ 
            py: 8, px: 4, textAlign: 'center', borderRadius: 3,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
            borderStyle: 'dashed'
          }}
        >
          <SentimentDissatisfiedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" fontWeight="700" color="text.secondary" gutterBottom>
            No Options Available
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ maxWidth: '400px', margin: '0 auto' }}>
            There are currently no active option contracts for the <strong>{activeFuture?.trading_symbol || selectedCommodity.label}</strong> contract.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CommodityOptionChainWidget;
